import { AssistantBenchmarkRunStatus } from '../../__generated__/globalTypes';

import type { TableSortDirection } from '../../components/Table/Table';
import type {
  IMopsAssistantBenchmarkArmCountsV1,
  IMopsAssistantBenchmarkArtifactV1,
  IMopsAssistantBenchmarkPerfComparisonV1,
  IMopsAssistantBenchmarkScenarioOutcomeV1,
  MopsAssistantBenchmarkDecision,
  MopsAssistantBenchmarkPerfMetric,
} from '@orn/global/assistant/MopsAssistantBenchmarkArtifact.js';

/**
 * Poll cadence for a run the operator is actively watching in the Benchmarks tool.
 * Each poll reconciles against GitHub, so a background consumer should choose its
 * own slower interval rather than reuse this one.
 */
export const BENCHMARK_POLL_INTERVAL_MS = 5000;

/** Terminal statuses — once a run reaches one, the detail poll stops. */
export const TERMINAL_BENCHMARK_STATUSES: ReadonlySet<AssistantBenchmarkRunStatus> = new Set([
  AssistantBenchmarkRunStatus.Passed,
  AssistantBenchmarkRunStatus.Failed,
  AssistantBenchmarkRunStatus.Inconclusive,
  AssistantBenchmarkRunStatus.Errored,
]);

export function isTerminalBenchmarkStatus(status: AssistantBenchmarkRunStatus): boolean {
  return TERMINAL_BENCHMARK_STATUSES.has(status);
}

/** Semantic tone for a status badge; the scene maps it to a theme text token. */
export type BenchmarkStatusTone = 'neutral' | 'running' | 'pass' | 'fail' | 'inconclusive' | 'error';

export function benchmarkStatusTone(status: AssistantBenchmarkRunStatus): BenchmarkStatusTone {
  switch (status) {
    case AssistantBenchmarkRunStatus.Passed:
      return 'pass';
    case AssistantBenchmarkRunStatus.Failed:
      return 'fail';
    case AssistantBenchmarkRunStatus.Inconclusive:
      return 'inconclusive';
    case AssistantBenchmarkRunStatus.Errored:
      return 'error';
    case AssistantBenchmarkRunStatus.Running:
      return 'running';
    default:
      // Pending (and any future status) reads as neutral until it advances.
      return 'neutral';
  }
}

/** Short display form for a commit SHA (first 10 chars); a shorter ref is left untouched. */
export function shortSha(sha: string): string {
  return sha.length > 10 ? sha.slice(0, 10) : sha;
}

const GITHUB_REPO_URL = 'https://github.com/onrepeatnow/orn-core';

/** GitHub commit page for a SHA or ref (GitHub resolves a ref such as `main` to its tip commit). */
export function githubCommitUrl(sha: string): string {
  return `${GITHUB_REPO_URL}/commit/${sha}`;
}

/**
 * Validate the start form. Returns an error message, or null when the pair is
 * runnable. Both commits are required and must differ — comparing a commit to
 * itself is never a benchmark.
 */
export function benchmarkFormError(baseSha: string, headSha: string): string | null {
  const base = baseSha.trim();
  const head = headSha.trim();
  if (base === '' || head === '') {
    return 'Enter both a base and a head commit.';
  }
  if (base === head) {
    return 'Base and head must be different commits.';
  }
  return null;
}

/** The verdict field arrives as a JSON scalar; narrow it to the shared contract for rendering. */
export function asBenchmarkVerdict(raw: unknown): IMopsAssistantBenchmarkArtifactV1 | null {
  return (raw as IMopsAssistantBenchmarkArtifactV1 | null | undefined) ?? null;
}

/** The drop as a percentage, rounded to one decimal — the value both the label and the tone read. */
function dropPercent(drop: number): number {
  return Math.round(drop * 1000) / 10;
}

/** Signed pass-rate drop (base − head) rendered as a percentage, or `—` when unknown. */
export function formatDrop(drop: number | null): string {
  if (drop === null) {
    return '—';
  }
  const pct = dropPercent(drop);
  return `${pct > 0 ? '+' : ''}${pct}%`;
}

/** Whether a drop reads as a regression, an improvement, or nothing worth tinting. */
export type BenchmarkDropTone = 'regression' | 'improvement' | 'neutral';

/**
 * Tone for a drop — positive means head passed less often, so it is the bad
 * direction. Keyed to the rounded percentage rather than the raw fraction, so a
 * value that renders `0%` is never tinted as though it moved.
 */
export function dropTone(drop: number | null): BenchmarkDropTone {
  if (drop === null) {
    return 'neutral';
  }
  const pct = dropPercent(drop);
  if (pct > 0) {
    return 'regression';
  }
  return pct < 0 ? 'improvement' : 'neutral';
}

/**
 * The perf metrics that are token cost rather than elapsed time, in render order,
 * with the abbreviation each gets in a cell — a scenario can be material on both
 * at once, so the cell has to name which moved.
 */
const TOKEN_METRICS: readonly { metric: MopsAssistantBenchmarkPerfMetric; short: string }[] = [
  { metric: 'outputTokens', short: 'out' },
  { metric: 'reasoningTokens', short: 'rsn' },
];

/** A scenario's material token-cost regressions, ready to render. */
export interface IMaterialTokenChange {
  /**
   * One entry per material metric, e.g. `rsn +142%`. Kept as a list rather than
   * one joined string so the cell can put each on its own line: the verdict pane
   * is the narrower half of a two-pane layout, and a single-line cell wide enough
   * for both metrics takes that width out of the scenario column.
   */
  parts: string[];
  /** Hover text carrying the medians behind each percentage. */
  detail: string;
  /** The larger of the material increases, as a fraction — what the column orders on. */
  worstIncrease: number;
}

/**
 * Material token-cost regressions for one scenario, or null when neither token
 * metric moved materially.
 *
 * Only an increase can be material — the gate tests `relativeIncrease` against a
 * positive per-metric threshold — so a value here always means head costs more
 * than base, and the sign is safe to hard-code.
 */
export function materialTokenChange(
  perf: readonly IMopsAssistantBenchmarkPerfComparisonV1[]
): IMaterialTokenChange | null {
  const parts: string[] = [];
  const details: string[] = [];
  let worstIncrease = 0;
  for (const { metric, short } of TOKEN_METRICS) {
    const comparison = perf.find((p) => p.metric === metric);
    if (comparison === undefined || !comparison.material || comparison.relativeIncrease === null) {
      continue;
    }
    const pct = Math.round(comparison.relativeIncrease * 100);
    parts.push(`${short} +${pct}%`);
    details.push(`${metric}: ${comparison.baseMedian ?? '—'} → ${comparison.headMedian ?? '—'} (+${pct}%)`);
    worstIncrease = Math.max(worstIncrease, comparison.relativeIncrease);
  }
  return parts.length === 0 ? null : { parts, detail: details.join('\n'), worstIncrease };
}

/**
 * A scenario the reader has no reason to look at: the ladder accepted it, its
 * pass rate is identical on both arms, head passed every measured run
 * (5/5 → 5/5), and it got no more expensive. Three exclusions are deliberate —
 * `main-unstable`, where an unchanged rate is not reassurance but the flakiness
 * itself; an unchanged rate below 100% (4/5 → 4/5), which is a scenario the
 * assistant is still failing part of the time; and a material token-cost
 * increase, which is a regression the pass rate cannot see. Without that last
 * one the fold hides most of what the Tokens column exists to surface: in a
 * whole-suite run, 9 of the 14 scenarios carrying a material token increase
 * passed every run on both arms.
 */
export function isUnchangedOutcome(outcome: IMopsAssistantBenchmarkScenarioOutcomeV1): boolean {
  return (
    outcome.decision === 'accept' &&
    outcome.drop === 0 &&
    outcome.head.okCount > 0 &&
    outcome.head.passCount === outcome.head.okCount &&
    materialTokenChange(outcome.perf) === null
  );
}

/** An arm's whole-run pass rate, or `null` when it completed no runs and the fraction has no denominator. */
function armPassRate(arm: IMopsAssistantBenchmarkArmCountsV1): number | null {
  return arm.okCount === 0 ? null : arm.passCount / arm.okCount;
}

/** The verdict-table column the reader is ordering on. */
export type BenchmarkSortColumn = 'scenario' | 'decision' | 'drop' | 'base' | 'head' | 'tokens';

export interface IBenchmarkSort {
  column: BenchmarkSortColumn;
  direction: TableSortDirection;
}

/** Opens on the scenarios head is failing most often. */
export const DEFAULT_BENCHMARK_SORT: IBenchmarkSort = { column: 'head', direction: 'asc' };

/**
 * Worst-first is the useful default on every column, but which direction that
 * is differs: a low pass rate is bad while a high drop is, so taking over a
 * column has to pick rather than reuse whatever the last one used.
 */
export function defaultSortDirection(column: BenchmarkSortColumn): TableSortDirection {
  return column === 'drop' || column === 'tokens' ? 'desc' : 'asc';
}

/** Severity order for the decision column, worst first. */
const DECISION_RANK: Record<MopsAssistantBenchmarkDecision, number> = {
  fail: 0,
  'main-unstable': 1,
  'inconclusive-error': 2,
  accept: 3,
};

/** True when the column has no value for this row, which is distinct from a low one. */
function isColumnEmpty(outcome: IMopsAssistantBenchmarkScenarioOutcomeV1, column: BenchmarkSortColumn): boolean {
  switch (column) {
    case 'drop':
      return outcome.drop === null;
    case 'base':
      return armPassRate(outcome.base) === null;
    case 'head':
      return armPassRate(outcome.head) === null;
    case 'tokens':
      return materialTokenChange(outcome.perf) === null;
    default:
      return false;
  }
}

/**
 * Ascending comparison on one column, for rows that both carry a value for it.
 * The switch is left exhaustive rather than given a fallback, so a new column
 * is a compile error instead of a silent zero.
 */
function compareColumn(
  a: IMopsAssistantBenchmarkScenarioOutcomeV1,
  b: IMopsAssistantBenchmarkScenarioOutcomeV1,
  column: BenchmarkSortColumn
): number {
  switch (column) {
    case 'scenario':
      return a.scenarioId.localeCompare(b.scenarioId);
    case 'decision':
      return DECISION_RANK[a.decision] - DECISION_RANK[b.decision];
    case 'drop':
      return (a.drop ?? 0) - (b.drop ?? 0);
    case 'base':
      return (armPassRate(a.base) ?? 0) - (armPassRate(b.base) ?? 0);
    case 'head':
      return (armPassRate(a.head) ?? 0) - (armPassRate(b.head) ?? 0);
    case 'tokens':
      return (materialTokenChange(a.perf)?.worstIncrease ?? 0) - (materialTokenChange(b.perf)?.worstIncrease ?? 0);
    // no default
  }
}

/**
 * Orders outcomes on one column. A row with no value for that column sorts last
 * in **both** directions rather than posing as a zero — flipping the direction
 * should reverse the ranking, not promote the unrankable. Scenario id settles
 * ties so the table doesn't reshuffle between polls.
 */
export function compareOutcomesBy(
  a: IMopsAssistantBenchmarkScenarioOutcomeV1,
  b: IMopsAssistantBenchmarkScenarioOutcomeV1,
  sort: IBenchmarkSort
): number {
  const aEmpty = isColumnEmpty(a, sort.column);
  const bEmpty = isColumnEmpty(b, sort.column);
  if (aEmpty !== bEmpty) {
    return aEmpty ? 1 : -1;
  }
  if (!aEmpty) {
    const ordered = compareColumn(a, b, sort.column);
    if (ordered !== 0) {
      return sort.direction === 'asc' ? ordered : -ordered;
    }
  }
  return a.scenarioId.localeCompare(b.scenarioId);
}

/** Outcomes split into the ones worth reading and the folded-away unchanged ones, each in the reader's sort order. */
export interface IPartitionedBenchmarkOutcomes {
  notable: IMopsAssistantBenchmarkScenarioOutcomeV1[];
  unchanged: IMopsAssistantBenchmarkScenarioOutcomeV1[];
}

export function partitionBenchmarkOutcomes(
  outcomes: readonly IMopsAssistantBenchmarkScenarioOutcomeV1[],
  sort: IBenchmarkSort = DEFAULT_BENCHMARK_SORT
): IPartitionedBenchmarkOutcomes {
  const sorted = [...outcomes].sort((a, b) => compareOutcomesBy(a, b, sort));
  return {
    notable: sorted.filter((outcome) => !isUnchangedOutcome(outcome)),
    unchanged: sorted.filter(isUnchangedOutcome),
  };
}
